export const DEFAULT_ARCGIS_ONLINE_PORTAL = 'https://www.arcgis.com/sharing/rest';
export const DEFAULT_AUTH_CALLBACK_PATH = '/auth/callback';

/**
 * Pre-registered ArcGIS Online application ID for the Velocity Migration
 * Assistant. Users can override this by registering their own AGOL app
 * (e.g. for organizational visibility), but the default lets anyone sign
 * in to ArcGIS Online without registering anything first.
 *
 * Enterprise sign-in still requires a per-portal app registration
 * because each portal has its own OAuth registry; there is no
 * equivalent ship-with-the-app client ID for Enterprise.
 */
export const DEFAULT_ARCGIS_ONLINE_CLIENT_ID = 'analytics4IoT';

const PORTAL_UI_PATH_SEGMENTS = new Set(['home', 'apps', 'oauth2', 'signin']);

export interface AuthRuntimeConfig {
  onlinePortalUrl: string;
  onlineClientId: string;
  authCallbackPath: string;
  tokenExpirationMinutes: number;
}

export function normalizePortalUrl(input: string): string {
  const trimmedInput = input.trim();
  if (!trimmedInput) {
    throw new Error('Portal URL is required');
  }

  const url = new URL(/^https?:\/\//i.test(trimmedInput) ? trimmedInput : `https://${trimmedInput}`);
  url.hash = '';
  url.search = '';

  const pathSegments = url.pathname.split('/').filter(Boolean);
  const sharingIndex = pathSegments.findIndex(
    (segment, index) => segment.toLowerCase() === 'sharing' && pathSegments[index + 1]?.toLowerCase() === 'rest',
  );

  if (sharingIndex >= 0) {
    url.pathname = `/${pathSegments.slice(0, sharingIndex + 2).join('/')}`;
    return trimTrailingSlash(url.toString());
  }

  while (pathSegments.length > 0 && PORTAL_UI_PATH_SEGMENTS.has(pathSegments[pathSegments.length - 1]!.toLowerCase())) {
    pathSegments.pop();
  }

  url.pathname = `/${[...pathSegments, 'sharing', 'rest'].join('/')}`;
  return trimTrailingSlash(url.toString());
}

export function requireClientId(clientId: string): string {
  const trimmedClientId = clientId.trim();
  if (!trimmedClientId) {
    throw new Error('ArcGIS OAuth client ID is required');
  }
  return trimmedClientId;
}

export function buildRedirectUri(origin: string, basePath = '', callbackPath = DEFAULT_AUTH_CALLBACK_PATH): string {
  const redirectBase = new URL(origin);
  redirectBase.pathname = joinPathSegments(basePath, callbackPath);
  redirectBase.search = '';
  redirectBase.hash = '';
  return redirectBase.toString();
}

/**
 * Resolves the SPA's mount base path. The lookup order is:
 *   1. `window.APP_BASE_PATH` (Node staging mode sets this from
 *      `process.env.APP_BASE_PATH`; static `__VMA_CONFIG__.basePath`
 *      flows through this same global via the bootstrap script).
 *   2. The pathname of `document.baseURI` (set by a `<base href>` in
 *      the page head; lets the host mount the SPA without setting
 *      `__VMA_CONFIG__`).
 *   3. Empty string (root deployment).
 *
 * The result is normalized to either `''` or `/<segment>` (no
 * trailing slash). Callers concatenate `'/auth/callback'` etc. onto
 * it, so the empty-string form maps to a root-mount.
 */
export function getConfiguredBasePath(
  win: Pick<Window, 'location'> & {
    APP_BASE_PATH?: string;
    document?: { baseURI?: string };
  },
): string {
  const explicit = win.APP_BASE_PATH?.trim();
  if (explicit) {
    return normalizePath(explicit);
  }
  const baseUri = win.document?.baseURI;
  if (baseUri) {
    try {
      const url = new URL(baseUri);
      if (url.origin === win.location.origin) {
        const pathname = url.pathname.replace(/\/+$/, '');
        if (pathname && pathname !== '/') {
          return normalizePath(pathname);
        }
      }
    } catch {
      // Ignore malformed baseURI; fall through to empty.
    }
  }
  return '';
}

export function getRuntimeAuthConfig(win: { ARCGIS_AUTH_CONFIG?: Partial<AuthRuntimeConfig> }): AuthRuntimeConfig {
  const config = win.ARCGIS_AUTH_CONFIG ?? {};
  return {
    onlinePortalUrl: config.onlinePortalUrl?.trim() || DEFAULT_ARCGIS_ONLINE_PORTAL,
    onlineClientId: config.onlineClientId?.trim() || DEFAULT_ARCGIS_ONLINE_CLIENT_ID,
    authCallbackPath: config.authCallbackPath?.trim() || DEFAULT_AUTH_CALLBACK_PATH,
    tokenExpirationMinutes: config.tokenExpirationMinutes ?? 20_160,
  };
}

function joinPathSegments(basePath: string, callbackPath: string): string {
  const normalizedBasePath = normalizePath(basePath);
  const normalizedCallbackPath = normalizePath(callbackPath) || DEFAULT_AUTH_CALLBACK_PATH;
  return `${normalizedBasePath}${normalizedCallbackPath}` || '/';
}

function normalizePath(path: string): string {
  const trimmedPath = path.trim();
  if (!trimmedPath || trimmedPath === '/') {
    return '';
  }
  return `/${trimmedPath.replace(/^\/+|\/+$/g, '')}`;
}

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}
